/**
 * Parent-app authentication bootstrap (Avida Care, Feb 2026).
 *
 * Implements the deep-link JWT exchange spec (§3b of the Feb-25-2026 reply):
 *
 *   1. RN clicks "Start Intake" in the parent app.
 *   2. Parent app opens our wizard at `https://intake.avidacare.com/?visit_id=...&token=<JWT>`.
 *   3. THIS module, on first load:
 *        a) extracts `?token=...` from the URL,
 *        b) POSTs it to /api/parent-auth/exchange to validate + get claims,
 *        c) stores token + claims in sessionStorage (NOT localStorage —
 *           keeps the wizard stateless on the parent's domain; NOT cookies —
 *           §3b explicitly forbids them),
 *        d) strips the `?token=` query param via history.replaceState so the
 *           token never lingers in the URL bar / browser history,
 *        e) installs a global fetch interceptor that auto-injects
 *           `Authorization: Bearer <token>` on every same-origin /api/* call.
 *
 * Preview / local mode: when the parent hasn't shipped real JWTs yet, the
 * exchange endpoint short-circuits to a stub claims object (`preview_mode:
 * true`). We treat that as "no auth installed" — the fetch interceptor stays
 * idle, so the rest of the app works exactly like it did before this module
 * existed.
 *
 * Why monkey-patch window.fetch instead of refactoring every call site?
 *   - There are ~30 components that build their own `${API}/api/...` URLs
 *     and call fetch directly. A central client wrapper would be cleaner
 *     long-term but is a much bigger change. The interceptor is contained:
 *     it only adds a header on /api/* requests when a token exists, and it
 *     never modifies non-API requests or requests that already carry an
 *     Authorization header (so component-local Bearer tokens, if any, win).
 */

const TOKEN_KEY = "wi-pa-parent-jwt-v1";
const CLAIMS_KEY = "wi-pa-parent-claims-v1";

function readSessionItem(key) {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch {
    /* sessionStorage unavailable (Safari private mode etc) — non-fatal */
    return "";
  }
}

function writeSessionItem(key, value) {
  try {
    if (value) {
      window.sessionStorage.setItem(key, value);
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch {
    /* sessionStorage unavailable — token simply won't persist across reloads */
  }
}

export function getStoredToken() {
  return readSessionItem(TOKEN_KEY);
}

export function getStoredClaims() {
  const raw = readSessionItem(CLAIMS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearParentAuth() {
  writeSessionItem(TOKEN_KEY, "");
  writeSessionItem(CLAIMS_KEY, "");
}

/**
 * Pull `?token=...` out of the current URL (if any) and remove it.
 * Returns the token string or "" if not present.
 *
 * Uses history.replaceState so the URL change doesn't fire a navigation
 * event and React Router doesn't unmount the page mid-mount.
 */
function extractAndStripToken() {
  if (typeof window === "undefined" || !window.location) return "";
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token") || "";
  if (!token) return "";
  url.searchParams.delete("token");
  // Preserve other params (visit_id, etc.) but drop the JWT from the bar.
  const cleaned = url.pathname + (url.search ? url.search : "") + url.hash;
  try {
    window.history.replaceState({}, document.title, cleaned);
  } catch {
    /* Some embedded browsers block replaceState — token still strips from
       memory; the URL bar may briefly show it but it's harmless. */
  }
  return token;
}

/**
 * POST the deep-link token to /api/parent-auth/exchange. On success,
 * persist token + claims to sessionStorage. On failure, clear both so the
 * fetch interceptor doesn't send a stale Bearer.
 *
 * Never throws — failure here just means we operate as anonymous; backend
 * preview-mode endpoints will still respond and the UI can render.
 */
async function exchangeToken(token) {
  if (!token) return null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (!apiBase) return null;
  try {
    const res = await fetch(`${apiBase}/api/parent-auth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      // Token rejected (expired / wrong aud / bad sig). Fall back to
      // anonymous mode rather than blocking the wizard outright — the RN
      // can still work the visit; the handoff endpoint will 401 and the
      // app surfaces that error in HandoffStatusCard.
      clearParentAuth();
      return null;
    }
    const body = await res.json();
    if (!body || body.ok !== true) {
      clearParentAuth();
      return null;
    }
    if (body.preview_mode) {
      // Stub claims from the backend — don't persist a fake token; let
      // the interceptor stay idle so preview behaves exactly like
      // before this module existed.
      return { previewMode: true, claims: body.claims };
    }
    writeSessionItem(TOKEN_KEY, token);
    writeSessionItem(CLAIMS_KEY, JSON.stringify(body.claims || {}));
    return { previewMode: false, claims: body.claims };
  } catch {
    /* Network error / CORS / etc. — non-fatal; operate anonymous. */
    clearParentAuth();
    return null;
  }
}

/**
 * Decide whether a fetch request should carry our Bearer header.
 *
 * Rules:
 *   - URL must point at our /api/* surface (same backend origin).
 *   - Caller must NOT have already set Authorization (component-local
 *     tokens win; the interceptor never overrides).
 */
function shouldInject(input, init) {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  if (!apiBase) return false;
  const url = typeof input === "string" ? input : (input && input.url) || "";
  if (!url.startsWith(apiBase + "/api/")) return false;
  // Caller-supplied header objects come in three shapes: plain {}, Headers
  // instance, or [[k,v]] tuples. Check all three before deciding to inject.
  const existing = (init && init.headers) || (input && input.headers);
  if (existing) {
    if (typeof existing.get === "function") {
      if (existing.get("Authorization")) return false;
    } else if (Array.isArray(existing)) {
      if (existing.some(([k]) => String(k).toLowerCase() === "authorization")) return false;
    } else if (typeof existing === "object") {
      const keys = Object.keys(existing).map((k) => k.toLowerCase());
      if (keys.includes("authorization")) return false;
    }
  }
  return true;
}

/**
 * Monkey-patch window.fetch to add `Authorization: Bearer <token>` to
 * /api/* calls when a parent token is present in sessionStorage.
 *
 * Idempotent: calling install* twice doesn't double-wrap.
 */
export function installFetchInterceptor() {
  if (typeof window === "undefined") return;
  if (window.__avidaFetchPatched) return;
  const original = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    const token = getStoredToken();
    if (!token || !shouldInject(input, init)) {
      return original(input, init);
    }
    const merged = { ...(init || {}) };
    const headers = new Headers((init && init.headers) || (input && input.headers) || undefined);
    headers.set("Authorization", `Bearer ${token}`);
    merged.headers = headers;
    return original(input, merged);
  };
  window.__avidaFetchPatched = true;
}

/**
 * Top-level boot routine. Call once, before React renders. Resolves once
 * the (optional) token exchange completes so any auth-aware UI that
 * reads claims on first paint sees them populated.
 */
export async function bootstrapParentAuth() {
  installFetchInterceptor();
  const token = extractAndStripToken();
  if (!token) {
    return getStoredClaims(); // may be a token from a previous tab session
  }
  const result = await exchangeToken(token);
  return (result && result.claims) || null;
}
